import { Card, Drawer, DrawerProps } from "antd";
import { useState } from "react";
import "../../styles/modalAnimations.css";
import { useOverlayKeyboardLock } from "../hooks/useOverlayKeyboardLock";
import { useOperationsWorkspace } from "./OperationsTransactionTabs";

interface StaticDrawerProps extends DrawerProps {
  children?: React.ReactNode;
}

/**
 * Static Drawer (Off-Canvas) - Cannot close by clicking outside, but supports Esc by default
 * Shows bounce animation when user tries to close via mask click
 */
export const StaticDrawer = ({ children, ...props }: StaticDrawerProps) => {
  const [isBouncing, setIsBouncing] = useState(false);
  const keyboardLockRef = useOverlayKeyboardLock(Boolean(props.open));
  const workspace = useOperationsWorkspace();
  const titleText = typeof props.title === "string" ? props.title : "";
  const isPrimaryEntry = /^(New|Create|Edit)\b/i.test(titleText);

  if (workspace?.mode === "entry" && isPrimaryEntry) {
    return <Card className="operations-inline-entry" title={props.title} extra={props.extra}>
      {children}
      {props.footer ? <div className="operations-inline-entry-footer">{props.footer}</div> : null}
    </Card>;
  }
  if (workspace?.mode === "data" && isPrimaryEntry) return null;

  const handleMaskClick = (e: React.MouseEvent) => {
    // Check if click is on the mask (outside the drawer)
    if ((e.target as HTMLElement).classList.contains("ant-drawer-mask")) {
      setIsBouncing(true);
      // Remove animation class after animation completes
      setTimeout(() => setIsBouncing(false), 500);
    }
  };

  return (
    <div
      onClick={handleMaskClick}
      className={isBouncing ? "drawer-bounce" : ""}
    >
      <Drawer
        {...props}
        rootClassName={`static-overlay-drawer${props.rootClassName ? ` ${props.rootClassName}` : ""}`}
        maskClosable={false}
        keyboard={props.keyboard ?? false}
        styles={{
          ...props.styles,
          content: {
            background: "var(--elevated-bg)",
            display: "flex",
            flexDirection: "column",
            ...props.styles?.content
          },
          header: {
            background: "var(--elevated-bg)",
            flexShrink: 0,
            ...props.styles?.header
          },
          body: {
            background: "var(--elevated-bg)",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            ...props.styles?.body
          },
          footer: {
            background: "var(--elevated-bg)",
            flexShrink: 0,
            ...props.styles?.footer
          }
        }}
        drawerRender={(node) => (
          <div
            ref={keyboardLockRef}
            tabIndex={-1}
            data-overlay-keyboard-lock="drawer"
            style={{ height: "100%" }}
          >
            {props.drawerRender ? props.drawerRender(node) : node}
          </div>
        )}
      >
        {children}
      </Drawer>
    </div>
  );
};
